import { getDownloadResources } from 'api/cms';
import Download from 'components/download';
import { H4 } from 'components/headings';
import Layout from 'components/layout';
import SEO from 'components/seo';
import SwitchTheme from 'components/switch-theme';
import ContentSection from 'components/ui/content-section';
import { Text } from 'components/ui/text';
import { useAnalytics } from 'hooks/use-analytics';
import { GetStaticProps, NextPage } from 'next';
import { TaggedDownloadResource } from 'types/sanity';
import { backgroundImages } from 'utils/config';

const PAGE_TITLE = 'Bushfire preparation checklists & kits';
const PAGE_DESCRIPTION =
  'View and download useful information sheets and bushfire preparation checklists to help you prepare for a fire emergency.';

interface Props {
  downloadResources: TaggedDownloadResource[];
}

const EmergencyResources: NextPage<Props> = ({ downloadResources }) => {
  const { logDownload } = useAnalytics();
  const hasDownloadResources = downloadResources && !!downloadResources.length;

  return (
    <Layout mainBackground={backgroundImages.treesBurn}>
      <SEO title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
      <ContentSection textAlign='center'>
        <H4 as='h1'>Bushfire preparation checklists and resources</H4>
        <Text size='large'>
          Having a bushfire plan set is the first step towards being ready to
          face such a harrowing situation.
        </Text>
        <Text size='large'>
          View and download useful emergency kit lists and checklists to help
          you prepare ahead of bushfire season. as well as information on what
          to on the day of a bushfire.
        </Text>
        {!hasDownloadResources && (
          <>
            <p>Currently there are no available resources.</p>
            <p>Please come back later.</p>
          </>
        )}
        {hasDownloadResources && (
          <>
            <SwitchTheme to='light'>
              {downloadResources.map(({ title, description, asset }) => (
                <Download
                  key={asset.assetId}
                  title={title}
                  description={description}
                  file={asset}
                  onClick={logDownload}
                />
              ))}
            </SwitchTheme>
            <br />
            <Text>
              Please note the documents above are in PDF format. If you have a
              disability and require this information in an alternative format,
              please email{' '}
              <a href='mailto:inclusion@dfes.wa.gov.au'>
                inclusion@dfes.wa.gov.au
              </a>
              .
            </Text>
          </>
        )}
      </ContentSection>
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<Props> = async () => {
  const downloadResources = await getDownloadResources();

  return {
    props: {
      downloadResources,
    },
  };
};

export default EmergencyResources;
